import React from 'react';
import axios from 'axios';
import { v4 } from 'uuid';
import { StoreContext } from './store';
import { CONTROLLER } from './hooks/useApi';

const StocksContext = React.createContext({ entries: {}, retry: () => {} });
const CHART_TIMEOUT_MS = 15000;

const useCharts = () => React.useContext(StocksContext);

const Stocks = ({ children }) => {
  const { store: { username, isAuth, paid, preferences: { stocks } = {} }, setResponse } = React.useContext(StoreContext);
  const scope = isAuth && paid ? username : null;
  const currentScope = React.useRef(scope);
  currentScope.current = scope;
  const [state, setState] = React.useState({ scope: null, entries: {} });
  const retryRef = React.useRef(() => {});

  React.useEffect(() => {
    let cancelled = false;
    const controllers = new Set();
    const pending = new Set();
    const queue = scope ? [...new Set(stocks || [])] : [];
    setState({ scope, entries: {} });

    const publish = (ticker, entry) => {
      if (!cancelled && currentScope.current === scope) {
        setState((previous) => ({ scope, entries: { ...previous.entries, [ticker]: entry } }));
      }
    };

    const load = async (ticker) => {
      if (cancelled || pending.has(ticker)) return;
      pending.add(ticker);
      const controller = new AbortController();
      controllers.add(controller);
      publish(ticker, { status: 'loading' });
      try {
        const response = await axios.request({
          ...CONTROLLER.CHART_SMALL,
          url: `/chart/small/${encodeURIComponent(ticker)}`,
          baseURL: '/api/',
          headers: { 'X-Request-Id': v4() },
          timeout: CHART_TIMEOUT_MS,
          signal: controller.signal,
        });
        if (cancelled || currentScope.current !== scope) return;
        setResponse(response.data);
        const data = response.data?.data;
        const usable = response.data?.success === true && data?.info?.ticker === ticker
          && Array.isArray(data.close) && data.close.some((point) => Array.isArray(point) && Number.isFinite(point[1]));
        publish(ticker, usable ? { status: 'available', data } : { status: 'unavailable' });
      } catch (error) {
        if (!cancelled && currentScope.current === scope && !controller.signal.aborted) {
          if (error.response?.data) setResponse(error.response.data);
          publish(ticker, { status: 'unavailable' });
        }
      } finally {
        pending.delete(ticker);
        controllers.delete(controller);
      }
    };

    retryRef.current = (ticker) => {
      if (scope && (stocks || []).includes(ticker)) load(ticker);
    };
    const worker = async () => {
      while (!cancelled && queue.length) await load(queue.shift());
    };
    const workerCount = Math.min(4, queue.length);
    for (let index = 0; index < workerCount; index += 1) worker();

    return () => {
      cancelled = true;
      retryRef.current = () => {};
      controllers.forEach((controller) => controller.abort());
    };
  }, [scope, stocks, setResponse]);

  const retry = React.useCallback((ticker) => retryRef.current(ticker), []);
  const value = React.useMemo(() => ({
    entries: state.scope === scope ? state.entries : {}, retry,
  }), [state, scope, retry]);
  return <StocksContext.Provider value={value}>{children}</StocksContext.Provider>;
};

export { Stocks, StocksContext, useCharts, CHART_TIMEOUT_MS };
